import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { useDataWithLoading } from '@/hooks/use-drill-data';
import { useClient } from '@/context/client-context';
import { useLocation } from 'react-router-dom';
import { clientDisplayLabel } from '@/lib/clients/roster-fields';
import { needsTagsToActiveFilters, activeFiltersToNeedsTags, contradictoryPostureLabel, seedNeedsFromClassification, NEEDS_CATEGORIES } from '@/lib/clients/needs-tags';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { updateClient } from '@/components/clients/services/clients-api';
import type { Client, ClientNeedsTag } from '@/lib/types';
import { Drill, Category, GenericModalContent, TagPreference } from './types/drill-types';
// Use centralized drill operations from the drill-library lib folder
import { useDrillOperations } from './lib/use-drill-operations';
import { loadTaxonomy } from '@/lib/taxonomy-loader';

import DrillCard from './DrillCard';
import Modal from '../shared/Modal';
import { DeleteIcon, DownloadIcon, UploadIcon } from '../shared/Icons';
import { useToast } from '@/hooks/use-toast';
import { exportDrillsToFile } from './lib';
import DrillImportModal from './DrillImportModal';
import BulkEditTagsPopin from './BulkEditTagsPopin';
import { BulkTagEditor, getCommonTags } from './BulkTagEditor';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import MultiSelect from './MultiSelect';
import { useSettings } from '@/components/settings/lib/settings-context';
import { useAdmin } from '@/contexts/AdminContext';
import { Heart, Filter, RotateCcw, Grid3x3, X, Tag } from 'lucide-react';
import { DomainCycleButton } from './DomainCycleButton';
import { useIsMobile } from '@/hooks/use-mobile';
import { ScrollArea } from '@/components/ui/scroll-area';
import { ContextualToolbar, ToolbarButtonGroup, ToolbarSeparator } from '@/components/ui/contextual-toolbar';
import { Separator } from '@/components/ui/separator';
import { UnifiedTag } from './UnifiedTag';
import { DrillCardPreferences, defaultDrillCardPreferences } from './types/drill-library-settings';
import { getTagColor } from '@/utils/drill-ui';
import { getDrillDomain, getTagValues, type CycledDomain } from '@/utils/drill-taxonomy';
import {
    drillSatisfiesAnyFilterValue,
    drillSatisfiesPostureFilterSet,
    drillSatisfiesPrimaryFilterKeys,
} from './lib/drill-filter-match';

interface DrillLibraryProps {
    user?: { uid: string; email?: string | null } | null;
    isOptionsDrawerOpen?: boolean;
    onOptionsDrawerToggle?: (open: boolean) => void;
    isViewPanelOpen?: boolean;
    onViewPanelToggle?: (open: boolean) => void;
    isFilterExpanded?: boolean;
    onFilterExpandedToggle?: (open: boolean) => void;
    onFilteredCountChange?: (count: number) => void;
    /** When set the library enters pick-one mode: each card shows a "Select" overlay button. */
    onPickDrill?: (drillId: string) => void;
}
import logger from '@/lib/logging';

/** Max drills rendered at once to prevent Chrome Error 5 (renderer OOM crash) with 400+ drills */
const DRILL_DISPLAY_LIMIT = 80;
/** Hard cap - never render more than this to avoid renderer crash */
const DRILL_DISPLAY_MAX = 200;

/** Main filter: Biomechanical Intent - subset with display labels (taxonomy value -> display) */
const MAIN_BIOMECHANICAL_OPTIONS = [
    'Anterior Expansion',
    'Posterior Expansion',
    'Occupancy',
    'Propulsion',
] as const;
const MAIN_BIOMECHANICAL_LABELS: Record<string, string> = {
    'Anterior Expansion': 'Anterior Rib Cage Expansion',
    'Posterior Expansion': 'Posterior Rib Cage Expansion',
    'Occupancy': 'Improve Occupancy (for non-lateralized side)',
    'Propulsion': 'Improve Propulsion (for lateralized side)',
};

/** Main filter: Posture - subset of Posture */
const MAIN_POSTURE_OPTIONS = ['Anterior Orientation', 'Swayback'] as const;

/** Sort options by taxonomy order (from RuleDoc-Drill-Taxonomy.json). Options not in taxonomy go last. */
function sortByTaxonomyOrder(
    options: string[],
    taxonomyOptions: string[] | undefined
): string[] {
    if (!taxonomyOptions?.length) return options;
    const order = new Map(taxonomyOptions.map((o, i) => [o, i]));
    return [...options].sort((a, b) => {
        const ia = order.has(a) ? order.get(a)! : Number.MAX_SAFE_INTEGER;
        const ib = order.has(b) ? order.get(b)! : Number.MAX_SAFE_INTEGER;
        return ia - ib;
    });
}

/** Display label fallbacks when taxonomy not yet loaded (category key -> short label) */
const CATEGORY_DISPLAY_LABELS: Record<string, string> = {};

/** Get display label for a category from taxonomy (e.g. "Posture" -> "Posture") */
function getCategoryDisplayLabel(categoryKey: string, taxonomy: { [key: string]: { displayLabel?: string } }): string {
    return taxonomy[categoryKey]?.displayLabel ?? CATEGORY_DISPLAY_LABELS[categoryKey] ?? categoryKey;
}

/** Main filter category keys for cascading options (order: Phase, Domain, Posture, Movement Limitation, Biomechanical Intent) */
const MAIN_FILTER_CATEGORIES = [
    { key: 'Phase', options: null as string[] | null, optionLabels: undefined as Record<string, string> | undefined },
    { key: 'Domain', options: null as string[] | null, optionLabels: undefined as Record<string, string> | undefined },
    { key: 'Posture', options: null as string[] | null, optionLabels: undefined as Record<string, string> | undefined },
    { key: 'Movement Limitation', options: null as string[] | null, optionLabels: undefined as Record<string, string> | undefined },
    { key: 'Biomechanical Intent', options: null as string[] | null, optionLabels: MAIN_BIOMECHANICAL_LABELS },
] as const;

/** Shown as top dropdowns; hidden from expanded tag grid to avoid duplication */
const MAIN_FILTER_CATEGORY_KEYS = new Set<string>(
    MAIN_FILTER_CATEGORIES.map(({ key }) => key)
);

/** Check if drill matches filters, excluding one category. Reuses same logic as filteredDrills. */
function drillMatchesFiltersExcluding(
    drill: Drill,
    activeFilters: { [key: string]: Set<string> },
    excludeCategory: string,
    searchTerm: string
): boolean {
    const searchMatch = !searchTerm || drill.name.toLowerCase().includes(searchTerm.toLowerCase()) || drill.description.toLowerCase().includes(searchTerm.toLowerCase());
    if (!searchMatch) return false;

    const activeFilterKeys = Object.keys(activeFilters).filter(key => key !== excludeCategory && activeFilters[key]?.size > 0);
    if (activeFilterKeys.length === 0) return true;

    const posturalFilterKey = 'Posture';
    const hasPosturalFilter = activeFilterKeys.includes(posturalFilterKey);
    if (hasPosturalFilter && !drillSatisfiesPostureFilterSet(drill, activeFilters[posturalFilterKey]!)) {
        return false;
    }

    const otherFilterKeys = activeFilterKeys.filter(key => key !== posturalFilterKey);
    const [primaryKeys, secondaryKeys] = otherFilterKeys.reduce<[string[], string[]]>((acc, key) => {
        if (['Phase', 'Domain'].includes(key)) acc[0].push(key);
        else acc[1].push(key);
        return acc;
    }, [[], []]);

    if (!drillSatisfiesPrimaryFilterKeys(drill, primaryKeys, activeFilters)) return false;

    const secondaryMatch =
        secondaryKeys.length === 0 ||
        secondaryKeys.every(key => drillSatisfiesAnyFilterValue(drill, key, activeFilters[key]!));

    return secondaryMatch;
}

const DrillLibrary: React.FC<DrillLibraryProps> = ({
    user,
    isOptionsDrawerOpen: externalIsOptionsDrawerOpen,
    onOptionsDrawerToggle,
    isViewPanelOpen: externalIsViewPanelOpen,
    onViewPanelToggle,
    isFilterExpanded: externalIsFilterExpanded,
    onFilterExpandedToggle,
    onFilteredCountChange,
    onPickDrill,
}) => {
    /** Embedded in drill-plan slot picker — compact UI, tap card to select. */
    const isPickMode = Boolean(onPickDrill);
    const { toast } = useToast();
    const { settings, setSettings } = useSettings();
    const { isAdminMode, isAuthorizedAdmin } = useAdmin();
    const { client: selectedClient, setClient } = useClient();
    const isMobile = useIsMobile();

    // Use centralized drill operations
    // canModifyDrills is true only when user is authorized admin AND has admin mode enabled
    const { db: drillDb, ops: drillOps, canModifyDrills } = useDrillOperations();

    // Use data hooks with loading state
    const { data: drills, loading: drillsLoading } = useDataWithLoading<Drill>('drills');
    const { data: categories, loading: categoriesLoading } = useDataWithLoading<Category>(user ? 'categories' : null);

    // Check if data is still loading
    const isLoading = drillsLoading || categoriesLoading;


    // Load saved filters from settings
    const savedFilters = settings.drillFilters || {
        activeFilters: {},
        showFavoritesOnly: false,
    };

    // Log if saved filters are being applied
    useEffect(() => {
        if (savedFilters && Object.keys(savedFilters.activeFilters || {}).length > 0) {
            const filterCount = Object.keys(savedFilters.activeFilters).length;
            const totalFilterValues = Object.values(savedFilters.activeFilters).reduce((sum, arr) => sum + (arr?.length || 0), 0);
            logger.debug(`🔍 Loaded saved drill filters from settings: ${filterCount} categories, ${totalFilterValues} total filter values`);
            logger.debug('   Active filters:', savedFilters.activeFilters);
            logger.debug('   If you see fewer drills than expected, these saved filters may be limiting results.');
            logger.debug('   Solution: Click the "Reset" or "Clear All" button in the Drill Library toolbar to clear filters');
        }
    }, []); // Only log once on mount

    const [searchTerm, setSearchTerm] = useState('');
    const [activeFilters, setActiveFilters] = useState<{ [key: string]: Set<string> }>(() => {
        // Convert saved array format back to Set format
        const filters: { [key: string]: Set<string> } = {};
        Object.entries(savedFilters.activeFilters).forEach(([key, values]) => {
            filters[key] = new Set(values);
        });
        return filters;
    });
    const [showFavoritesOnly, setShowFavoritesOnly] = useState(savedFilters.showFavoritesOnly);

    // Selection mode is automatically enabled when admin mode is on
    const effectiveSelectionMode = isAdminMode;
    const [selectedDrillIds, setSelectedDrillIds] = useState<Set<string>>(new Set());

    const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
    // Use external state for filter expansion if provided, otherwise use internal state
    const [internalIsFilterExpanded, setInternalIsFilterExpanded] = useState(false);
    const isFilterExpanded = externalIsFilterExpanded !== undefined ? externalIsFilterExpanded : internalIsFilterExpanded;
    const handleFilterExpandedToggle = (open: boolean) => {
        if (onFilterExpandedToggle) {
            onFilterExpandedToggle(open);
        } else {
            setInternalIsFilterExpanded(open);
        }
    };
    const [isBulkEditPopinExpanded, setIsBulkEditPopinExpanded] = useState(false);
    const [isBulkTagEditorOpen, setIsBulkTagEditorOpen] = useState(false);
    // Use external state for view panel if provided, otherwise use internal state
    const [internalIsViewPanelOpen, setInternalIsViewPanelOpen] = useState(false);
    const isViewPanelOpen = externalIsViewPanelOpen !== undefined ? externalIsViewPanelOpen : internalIsViewPanelOpen;
    const handleViewPanelToggle = (open: boolean) => {
        if (onViewPanelToggle) {
            onViewPanelToggle(open);
        } else {
            setInternalIsViewPanelOpen(open);
        }
    };
    const [internalIsOptionsDrawerOpen, setInternalIsOptionsDrawerOpen] = useState(false);
    const isOptionsDrawerOpen = externalIsOptionsDrawerOpen !== undefined ? externalIsOptionsDrawerOpen : internalIsOptionsDrawerOpen;
    const handleOptionsDrawerToggle = (open: boolean) => {
        if (onOptionsDrawerToggle) {
            onOptionsDrawerToggle(open);
        } else {
            setInternalIsOptionsDrawerOpen(open);
        }
    };
    const [focusedDrillIndex, setFocusedDrillIndex] = useState<number | null>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);

    // Enhanced features
    const [chatSearchTerm, setChatSearchTerm] = useState('');
    const [chatSearchResults, setChatSearchResults] = useState<Drill[] | null>(null);
    const [sortOption, setSortOption] = useState<'name-asc' | 'name-desc' | 'date-desc' | 'date-asc'>('name-asc');
    const [genericModalContent, setGenericModalContent] = useState<GenericModalContent | null>(null);
    const [tagPreferences, setTagPreferences] = useState<{ [key: string]: TagPreference }>({});
    const [isImportModalOpen, setIsImportModalOpen] = useState(false);

    // Dynamic taxonomy state (includes displayLabel for filter placeholders)
    const [taxonomy, setTaxonomy] = useState<{ [key: string]: { type: string; options: string[]; displayLabel?: string } }>({});
    // Colorization category (user configurable)
    const [colorizationCategory, setColorizationCategory] = useState<string>((settings as any)?.colorizationCategory || 'Domain');

    // Load dynamic taxonomy on mount
    useEffect(() => {
        const initializeTaxonomy = async () => {
            const data = await loadTaxonomy();
            if (data.categories) {
                setTaxonomy(data.categories);
            }
        };
        initializeTaxonomy();
    }, []);

    // Save colorization category to settings
    useEffect(() => {
        setSettings(prev => ({ ...prev, colorizationCategory }));
    }, [colorizationCategory, setSettings]);

    // On mobile, always use list mode; on desktop, use saved preference or default to grid
    const drillLayoutMode = isMobile ? 'list' : (settings.drillLayoutMode ?? 'grid');
    const drillGridColumns = Math.min(Math.max(settings.drillGridColumns ?? 3, 2), 5);
    const drillCardPreferences: DrillCardPreferences = useMemo(
        () => ({
            ...defaultDrillCardPreferences,
            ...(settings.drillCardPreferences ?? {}),
        }),
        [settings.drillCardPreferences]
    );

    const gridColumnClasses: Record<number, string> = {
        2: 'grid-cols-1 md:grid-cols-2 justify-items-center',
        3: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 justify-items-center',
        4: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 justify-items-center',
        5: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 justify-items-center',
    };

    const quickPresetConfigs = [
        {
            label: 'Phase 1 Only',
            onSelect: () => {
                setActiveFilters({ 'Phase': new Set(['Phase 1']) });
                toast({ title: 'Phase 1 drills selected' });
            },
        },
        {
            label: 'Positional Only',
            onSelect: () => {
                setActiveFilters({ 'Domain': new Set(['Positional']) });
                toast({ title: 'Positional drills selected' });
            },
        },
        {
            label: 'Supine Only',
            onSelect: () => {
                setActiveFilters({ 'Body Position': new Set(['Supine']) });
                toast({ title: 'Supine drills selected' });
            },
        },
        {
            label: 'My Favorites',
            onSelect: () => {
                setShowFavoritesOnly(true);
                toast({ title: 'Showing favorites' });
            },
        },
    ];

    const cycleLibraryDomain = useCallback((next: CycledDomain) => {
        setActiveFilters(prev => ({
            ...prev,
            Domain: new Set([next]),
        }));
        toast({ title: `${next} drills selected` });
    }, [toast]);

    // Load taxonomy and colors on mount
    useEffect(() => {
        const initializeTaxonomy = async () => {
            const data = await loadTaxonomy();
            if (data.categories) {
                setTaxonomy(data.categories);
            }

            // Load colors
            if (typeof window !== 'undefined') {
                const { loadColors } = await import('@/utils/drill-ui');
                await loadColors();
            }
        };
        initializeTaxonomy();
    }, []);

    // Favorites functionality
    const favoriteDrills = settings.favoriteDrills || [];

    const toggleFavorite = (drillId: string) => {
        setSettings(prev => {
            const currentFavorites = prev.favoriteDrills || [];
            const isFavorite = currentFavorites.includes(drillId);

            if (isFavorite) {
                return {
                    ...prev,
                    favoriteDrills: currentFavorites.filter(id => id !== drillId)
                };
            } else {
                return {
                    ...prev,
                    favoriteDrills: [...currentFavorites, drillId]
                };
            }
        });
    };

    // True while a client-needs pre-filter is active (set by the client-view entry
    // effect below, cleared by exitClientFilter). Declared here because the persist
    // effect must consult it.
    const clientFilterAppliedRef = useRef(false);
    const clientFilterSnapshotRef = useRef<{
        activeFilters: { [key: string]: string[] };
        showFavoritesOnly: boolean;
    } | null>(null);

    // Save filters to settings whenever they change — except while a client view is
    // active. The client's needs-derived filters are transient: persisting them is
    // what used to leak a client's Phase filter into the user's saved filters when
    // the tab closed before the unmount restore could run (PR #7515 bug 2).
    useEffect(() => {
        if (clientFilterAppliedRef.current) return;

        // Convert Set format to array format for storage
        const filtersToSave: { [key: string]: string[] } = {};
        Object.entries(activeFilters).forEach(([key, valueSet]) => {
            filtersToSave[key] = Array.from(valueSet);
        });

        setSettings(prev => ({
            ...prev,
            drillFilters: {
                activeFilters: filtersToSave,
                showFavoritesOnly,
            }
        }));
    }, [activeFilters, showFavoritesOnly, setSettings]);

    // Reset filters to default
    const resetFilters = () => {
        setActiveFilters({});
        setShowFavoritesOnly(false);
        setChatSearchTerm('');
    };

    // --- Client needs soft pre-filter (Phase 2B) ---------------------------------
    // When opened via "Open Drill Library for {client}", apply the client's needs as
    // a soft pre-filter and show a banner. The user's saved filters are snapshotted
    // and restored on exit/unmount so the client view never permanently clobbers them.
    const location = useLocation();
    const [clientFilterName, setClientFilterName] = useState<string | null>(null);

    useEffect(() => {
        if (clientFilterAppliedRef.current) return;
        const state = location.state as { loadClientNeeds?: boolean } | null;
        if (!state?.loadClientNeeds) return;

        // selectedClient normally comes from context (set by the roster/profile
        // "Open library" action before navigating). On some mobile navigations the
        // context client can be momentarily unavailable when this route mounts —
        // fall back to the persisted client so the needs pre-filter still applies
        // instead of opening an unfiltered "Select Client" library.
        let client: Client | null = selectedClient;
        let usedPersistedFallback = false;
        if (!client && typeof window !== 'undefined') {
            try {
                const stored = window.localStorage.getItem('selectedClient');
                if (stored) {
                    client = JSON.parse(stored) as Client;
                    usedPersistedFallback = true;
                }
            } catch (error) {
                logger.warn('[DrillLibrary] Could not read persisted client for needs pre-filter', error);
            }
        }

        logger.debug('[DrillLibrary] loadClientNeeds requested', {
            category: 'drill-library',
            hasContextClient: Boolean(selectedClient),
            usedPersistedFallback,
            clientId: client?.id ?? null,
            needsCount: client?.needsTags?.length ?? 0,
        });

        // No client available yet (context still hydrating) — bail without marking
        // applied so this effect re-runs once selectedClient loads.
        if (!client) return;

        clientFilterAppliedRef.current = true;

        // Curated needs first; fall back to classification-derived suggestions (e.g. APT → Anterior Orientation).
        // Guarded: a malformed client/assessment must never blank the library — degrade to an unfiltered view.
        let needsFilters: { [category: string]: Set<string> } = {};
        try {
            const needs =
                (client.needsTags?.length ?? 0) > 0
                    ? client.needsTags!
                    : seedNeedsFromClassification(client);
            needsFilters = needsTagsToActiveFilters(needs);
        } catch (error) {
            logger.error('Failed to seed drill filters from client needs', error);
        }

        // Snapshot the user's saved filters minus this client's needs. Settings
        // persisted before the persist-effect gate existed can already contain the
        // client's needs (a tab close skipped the unmount restore), so laundering
        // the snapshot here means every exit path — Clear and unmount — restores
        // the user's genuine filters rather than re-applying the contamination.
        const laundered: { [key: string]: string[] } = {};
        Object.entries(settings.drillFilters?.activeFilters ?? {}).forEach(([key, values]) => {
            const remaining = (values ?? []).filter((value) => !needsFilters[key]?.has(value));
            if (remaining.length > 0) laundered[key] = remaining;
        });
        clientFilterSnapshotRef.current = {
            activeFilters: laundered,
            showFavoritesOnly: settings.drillFilters?.showFavoritesOnly ?? false,
        };

        setClientFilterName(clientDisplayLabel(client));
        // Sync context from the persisted fallback so the header + downstream
        // (Edit needs, planner) reflect the client instead of "Select Client".
        if (usedPersistedFallback) setClient(client);
        if (Object.keys(needsFilters).length > 0) {
            setActiveFilters(needsFilters);
            setShowFavoritesOnly(false);
        }
    }, [location.state, selectedClient, settings.drillFilters, setClient]);

    const exitClientFilter = useCallback(() => {
        // The entry snapshot was laundered (client needs subtracted) when it was
        // taken, so restoring it verbatim is what returns the user's own filters.
        const snapshot = clientFilterSnapshotRef.current;
        // Clear the flag before the state updates so the persist effect resumes
        // saving the restored filters on the next commit.
        clientFilterAppliedRef.current = false;
        if (snapshot) {
            const restored: { [key: string]: Set<string> } = {};
            Object.entries(snapshot.activeFilters).forEach(([key, values]) => {
                restored[key] = new Set(values);
            });
            setActiveFilters(restored);
            setShowFavoritesOnly(snapshot.showFavoritesOnly);
        } else {
            setActiveFilters({});
            setShowFavoritesOnly(false);
        }
        clientFilterSnapshotRef.current = null;
        editEntrySnapshotRef.current = null;
        lastWrittenNeedsKeyRef.current = null;
        oppositePostureRef.current = null;
        contradictionAcceptedRef.current = false;
        setPendingContradiction(null);
        setEditingNeeds(false);
        setClientFilterName(null);
    }, []);

    // Restore the user's saved filters on unmount if a client view was active.
    useEffect(() => {
        return () => {
            const snapshot = clientFilterSnapshotRef.current;
            if (snapshot) {
                setSettings(prev => ({
                    ...prev,
                    drillFilters: {
                        activeFilters: snapshot.activeFilters,
                        showFavoritesOnly: snapshot.showFavoritesOnly,
                    },
                }));
            }
        };
    }, [setSettings]);

    // --- Library → Client write-back (Phase 2B, "Editing {client}" bound mode) ---
    // Opt-in: general browsing never writes back. While editing, toggling an
    // allow-listed needs filter (Posture / Biomechanical Intent / Movement Limitation)
    // updates the client's needsTags live (debounced). Revert restores the entry
    // snapshot; Done exits the bound mode. Only needs categories write back.
    const [editingNeeds, setEditingNeeds] = useState(false);
    const editEntrySnapshotRef = useRef<ClientNeedsTag[] | null>(null);
    const lastWrittenNeedsKeyRef = useRef<string | null>(null);
    // Guard: the Posture label that contradicts this client's orientation (APT↔SB).
    const oppositePostureRef = useRef<string | null>(null);
    // Once the practitioner confirms the contradiction, don't re-prompt this session.
    const contradictionAcceptedRef = useRef(false);
    const [pendingContradiction, setPendingContradiction] = useState<
        { label: string; needs: ClientNeedsTag[]; key: string } | null
    >(null);
    // Distinguishes a button decision from an ESC/overlay dismissal so confirming
    // is not immediately undone by the dialog's close callback.
    const contradictionDecidedRef = useRef(false);

    const needsKey = useCallback(
        (needs: ClientNeedsTag[]) =>
            needs.map(t => `${t.category}::${t.value}`).sort().join('|'),
        []
    );

    const persistClientNeeds = useCallback(
        async (needs: ClientNeedsTag[]) => {
            if (!selectedClient) return;
            const audit = {
                needsTags: needs,
                needsTagsUpdatedAt: new Date().toISOString(),
                needsTagsUpdatedBy: user?.email || user?.uid || 'unknown',
            };
            try {
                await updateClient(selectedClient.id, audit);
                setClient({ ...selectedClient, ...audit });
            } catch (e) {
                logger.error('Failed to write client needs from library', e);
                toast({
                    title: 'Could not save needs',
                    description: 'The client was not updated. Check your connection or permissions.',
                    variant: 'destructive',
                });
            }
        },
        [selectedClient, user, setClient, toast]
    );

    // Keep a stable ref so the live-write effect does not depend on the changing
    // selectedClient/persist identity (which would cause a write→setClient→write loop).
    const persistClientNeedsRef = useRef(persistClientNeeds);
    useEffect(() => {
        persistClientNeedsRef.current = persistClientNeeds;
    }, [persistClientNeeds]);

    useEffect(() => {
        if (!editingNeeds) return;
        const needs = activeFiltersToNeedsTags(activeFilters);
        const key = needsKey(needs);
        if (key === lastWrittenNeedsKeyRef.current) return;

        // Guard: warn before stamping a posture that contradicts the client's
        // classification (e.g. Swayback onto an APT client). Browse stays free —
        // this only fires while editing the client's needs.
        const opposite = oppositePostureRef.current;
        const addsContradiction =
            !!opposite &&
            !contradictionAcceptedRef.current &&
            needs.some((t) => t.category === 'Posture' && t.value === opposite);
        if (addsContradiction && opposite) {
            contradictionDecidedRef.current = false;
            setPendingContradiction({ label: opposite, needs, key });
            return;
        }

        const timer = window.setTimeout(() => {
            lastWrittenNeedsKeyRef.current = key;
            void persistClientNeedsRef.current(needs);
        }, 600);
        return () => window.clearTimeout(timer);
    }, [activeFilters, editingNeeds, needsKey]);

    const confirmContradiction = useCallback(() => {
        const pending = pendingContradiction;
        if (!pending) return;
        contradictionDecidedRef.current = true;
        contradictionAcceptedRef.current = true;
        lastWrittenNeedsKeyRef.current = pending.key;
        void persistClientNeedsRef.current(pending.needs);
        setPendingContradiction(null);
    }, [pendingContradiction]);

    const cancelContradiction = useCallback(() => {
        const opposite = pendingContradiction?.label;
        contradictionDecidedRef.current = true;
        setPendingContradiction(null);
        if (!opposite) return;
        // Undo the contradictory pick so the client is never written with it.
        setActiveFilters((prev) => {
            const next: { [key: string]: Set<string> } = {};
            Object.entries(prev).forEach(([category, values]) => {
                const set = new Set(values as Set<string>);
                if (category === 'Posture') set.delete(opposite);
                if (set.size > 0) next[category] = set;
            });
            return next;
        });
    }, [pendingContradiction]);

    const startEditingNeeds = useCallback(() => {
        if (!selectedClient) return;
        const entryNeeds = selectedClient.needsTags ?? [];
        editEntrySnapshotRef.current = entryNeeds;
        lastWrittenNeedsKeyRef.current = needsKey(activeFiltersToNeedsTags(activeFilters));
        oppositePostureRef.current = contradictoryPostureLabel(selectedClient);
        // If the client already carries the contradictory posture, treat it as
        // previously accepted so we don't nag about pre-existing data.
        contradictionAcceptedRef.current = entryNeeds.some(
            (t) => t.category === 'Posture' && t.value === oppositePostureRef.current
        );
        setEditingNeeds(true);
        toast({
            title: `Editing ${clientDisplayLabel(selectedClient)}'s needs`,
            description: 'Filter changes (Posture, Intent, Movement) save to the client automatically.',
        });
    }, [selectedClient, activeFilters, needsKey, toast]);

    const revertNeeds = useCallback(() => {
        const snapshot = editEntrySnapshotRef.current ?? [];
        const snapshotFilters = needsTagsToActiveFilters(snapshot);
        const needsCategories = new Set<string>(NEEDS_CATEGORIES);
        setActiveFilters((prev) => {
            const next: { [key: string]: Set<string> } = {};
            // Preserve any non-needs (browse-only) filters the user had applied.
            Object.entries(prev).forEach(([category, values]) => {
                if (!needsCategories.has(category)) {
                    next[category] = new Set(values as Set<string>);
                }
            });
            // Restore needs categories to the entry snapshot.
            Object.entries(snapshotFilters).forEach(([category, values]) => {
                next[category] = new Set(values);
            });
            return next;
        });
        toast({ title: 'Reverted', description: 'Needs restored to where you started editing.' });
    }, [toast]);

    const finishEditingNeeds = useCallback(() => {
        // Flush any pending (debounced) change: turning off editingNeeds cancels the
        // 600ms write timer, so a quick Done right after a toggle would otherwise drop
        // the last edit while still toasting "saved". Skip while a contradiction is
        // unresolved — that pick must be confirmed/cancelled first.
        if (!pendingContradiction) {
            const needs = activeFiltersToNeedsTags(activeFilters);
            const key = needsKey(needs);
            if (key !== lastWrittenNeedsKeyRef.current) {
                lastWrittenNeedsKeyRef.current = key;
                void persistClientNeedsRef.current(needs);
            }
        }
        setEditingNeeds(false);
        editEntrySnapshotRef.current = null;
        lastWrittenNeedsKeyRef.current = null;
        oppositePostureRef.current = null;
        contradictionAcceptedRef.current = false;
        setPendingContradiction(null);
        toast({
            title: 'Needs saved',
            description: `${selectedClient ? clientDisplayLabel(selectedClient) : 'Client'}'s needs have been updated.`,
        });
    }, [selectedClient, activeFilters, needsKey, pendingContradiction]);

    // Create filtered taxonomy based on actual drill data
    const usedTaxonomy = useMemo(() => {
        const usedCategories: { [key: string]: Set<string> } = {};

        // Analyze all drills to find actually used categories and values
        drills.forEach(drill => {
            if (drill.tags) {
                Object.entries(drill.tags).forEach(([category, value]) => {
                    if (!usedCategories[category]) {
                        usedCategories[category] = new Set();
                    }

                    // Handle both string and string[] values
                    if (Array.isArray(value)) {
                        value.forEach(v => usedCategories[category].add(v));
                    } else if (typeof value === 'string') {
                        usedCategories[category].add(value);
                    }
                });
            }
        });

        // Filter taxonomy to only include categories and options that are actually used
        const filteredTaxonomy: { [key: string]: { type: string; options: string[] } } = {};

        Object.entries(taxonomy).forEach(([category, config]) => {
            if (usedCategories[category] && usedCategories[category].size > 0) {
                const usedOptions = Array.from(usedCategories[category]);
                // Filter options to only include those that exist in the drill data
                const availableOptions = config.options.filter(option =>
                    usedOptions.includes(option)
                );

                if (availableOptions.length > 0) {
                    filteredTaxonomy[category] = {
                        ...config,
                        options: sortByTaxonomyOrder(availableOptions, config.options)
                    };
                }
            }
        });

        return filteredTaxonomy;
    }, [drills, taxonomy]);

    // Cascading options: for each main filter, only show options that exist in drills matching the OTHER filters
    const availableOptionsForMainFilters = useMemo(() => {
        const baseDrills = chatSearchResults ?? drills;
        const searchTermForFilter = chatSearchTerm.trim();
        const result: { [category: string]: string[] } = {};

        for (const { key } of MAIN_FILTER_CATEGORIES) {
            const matchingDrills = baseDrills.filter(d =>
                drillMatchesFiltersExcluding(d, activeFilters, key, searchTermForFilter)
            );
            const values = new Set<string>();
            matchingDrills.forEach(drill => {
                const vals = getTagValues(drill, key);
                vals.forEach(v => values.add(v));
            });
            const taxonomyOptions = taxonomy[key]?.options;
            result[key] = sortByTaxonomyOrder(Array.from(values), taxonomyOptions);
        }

        return result;
    }, [drills, chatSearchResults, chatSearchTerm, activeFilters, taxonomy]);

    /** Options for a main filter. When no other filters are selected: show full taxonomy. When filters are active: cascade to options from matching drills. */
    const getMainFilterOptions = useCallback((categoryKey: string, taxonomySubset: string[] | null) => {
        const fromDrills = availableOptionsForMainFilters[categoryKey] ?? [];
        const selected = Array.from(activeFilters[categoryKey] || []);
        const hasOtherActiveFilters = Object.keys(activeFilters).some(k => k !== categoryKey && (activeFilters[k]?.size ?? 0) > 0);

        // When no other filters selected: show all taxonomy options so user can see and select any value
        if (!hasOtherActiveFilters) {
            const fullPool = taxonomySubset && taxonomySubset.length > 0
                ? taxonomySubset
                : (taxonomy[categoryKey]?.options ?? usedTaxonomy[categoryKey]?.options ?? fromDrills);
            const valid = new Set([...selected, ...fullPool]);
            return sortByTaxonomyOrder(Array.from(valid), taxonomy[categoryKey]?.options);
        }

        // When filters active: cascade - only show options from drills matching other filters
        const pool = taxonomySubset && taxonomySubset.length > 0 ? taxonomySubset : (usedTaxonomy[categoryKey]?.options ?? taxonomy[categoryKey]?.options ?? fromDrills);
        const cascadeOptions = fromDrills.length > 0
            ? pool.filter(o => fromDrills.includes(o))
            : pool; // Fallback when no matching drills have this tag
        const valid = new Set([...selected, ...cascadeOptions]);
        return sortByTaxonomyOrder(Array.from(valid), taxonomy[categoryKey]?.options);
    }, [availableOptionsForMainFilters, activeFilters, usedTaxonomy, taxonomy]);

    // Get available categories for colorization (must be after usedTaxonomy is defined)
    const availableCategories = useMemo(() => {
        return Object.keys(usedTaxonomy).filter(cat => cat !== 'Sub-Category');
    }, [usedTaxonomy]);

    const filteredDrills = useMemo(() => {
        const baseDrills = chatSearchResults ?? drills;

        const filtered = baseDrills.filter(drill => {
            // Filter by favorites first if enabled
            if (showFavoritesOnly && !favoriteDrills.includes(drill.id)) {
                return false;
            }

            const searchMatch = !searchTerm || drill.name.toLowerCase().includes(searchTerm.toLowerCase()) || drill.description.toLowerCase().includes(searchTerm.toLowerCase());

            const activeFilterKeys = Object.keys(activeFilters).filter(key => activeFilters[key]?.size > 0);
            if (activeFilterKeys.length === 0) return searchMatch;

            const posturalFilterKey = 'Posture';
            const hasPosturalFilter = activeFilterKeys.includes(posturalFilterKey);
            if (hasPosturalFilter && !drillSatisfiesPostureFilterSet(drill, activeFilters[posturalFilterKey]!)) {
                return false;
            }

            const otherFilterKeys = activeFilterKeys.filter(key => key !== posturalFilterKey);

            const [primaryKeys, secondaryKeys] = otherFilterKeys.reduce<[string[], string[]]>((acc, key) => {
                if (['Phase', 'Domain'].includes(key)) {
                    acc[0].push(key);
                } else {
                    acc[1].push(key);
                }
                return acc;
            }, [[], []]);

            if (!drillSatisfiesPrimaryFilterKeys(drill, primaryKeys, activeFilters)) return false;

            const secondaryMatch =
                secondaryKeys.length === 0 ||
                secondaryKeys.every(key => drillSatisfiesAnyFilterValue(drill, key, activeFilters[key]!));

            return searchMatch && secondaryMatch;
        });

        return [...filtered].sort((a, b) => {
            switch (sortOption) {
                case 'name-asc':
                    return a.name.localeCompare(b.name);
                case 'name-desc':
                    return b.name.localeCompare(a.name);
                case 'date-desc':
                    return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
                case 'date-asc':
                    return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
                default:
                    return 0;
            }
        });


    }, [drills, searchTerm, activeFilters, chatSearchResults, sortOption, showFavoritesOnly, favoriteDrills]);

    // Notify parent of filtered count change (use useEffect for side effects)
    useEffect(() => {
        if (onFilteredCountChange) {
            onFilteredCountChange(filteredDrills.length);
        }
    }, [filteredDrills.length, onFilteredCountChange]);

    // Load tag preferences from localStorage
    useEffect(() => {
        try {
            const storedPrefs = localStorage.getItem('tagPreferences');
            if (storedPrefs) {
                setTagPreferences(JSON.parse(storedPrefs));
            }
        } catch (e) {
            logger.error("Failed to load tag preferences from localStorage", e);
        }
    }, []);

    // Auto-search effect for dynamic search
    useEffect(() => {
        const query = chatSearchTerm.trim();
        if (!query) {
            setChatSearchResults(null);
            return;
        }
        const tokens = query.toLowerCase().split(/\s+/).filter(Boolean);
        const results = drills.filter(d => {
            let text = `${d.name || ''} ${d.description || ''} ${d.transcript || ''}`;
            if (d.tags) {
                Object.values(d.tags).forEach(val => {
                    text += ` ${Array.isArray(val) ? val.join(' ') : val}`;
                });
            }
            const ltext = text.toLowerCase();
            return tokens.every(tok => ltext.includes(tok));
        });
        setChatSearchResults(results);
    }, [chatSearchTerm, drills]);

    // Keyboard shortcuts for faster navigation
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            // Don't handle shortcuts when typing in inputs, textareas, or when modals are open
            const target = e.target as HTMLElement;
            if (
                target.tagName === 'INPUT' ||
                target.tagName === 'TEXTAREA' ||
                target.isContentEditable ||
                isBulkTagEditorOpen ||
                isDeleteConfirmOpen ||
                isImportModalOpen ||
                genericModalContent
            ) {
                return;
            }

            // Focus search with '/' key
            if (e.key === '/' && !e.ctrlKey && !e.metaKey && !e.altKey) {
                e.preventDefault();
                searchInputRef.current?.focus();
                return;
            }

            // Escape to clear selection or close filters
            if (e.key === 'Escape') {
                if (isAdminMode && selectedDrillIds.size > 0) {
                    setSelectedDrillIds(new Set());
                    setIsBulkEditPopinExpanded(false);
                    setIsBulkTagEditorOpen(false);
                } else if (isFilterExpanded) {
                    handleFilterExpandedToggle(false);
                } else if (isViewPanelOpen) {
                    handleViewPanelToggle(false);
                }
                setFocusedDrillIndex(null);
                return;
            }

            // Arrow keys to navigate between drills (works in both admin and non-admin mode)
            if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                e.preventDefault();
                const currentIndex = focusedDrillIndex ?? -1;
                let nextIndex: number;

                if (e.key === 'ArrowDown') {
                    nextIndex = currentIndex < filteredDrills.length - 1 ? currentIndex + 1 : 0;
                } else {
                    nextIndex = currentIndex > 0 ? currentIndex - 1 : filteredDrills.length - 1;
                }

                setFocusedDrillIndex(nextIndex);
                // Scroll into view
                const drillElement = document.querySelector(`[data-drill-id="${filteredDrills[nextIndex]?.id}"]`);
                drillElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                return;
            }

            // In admin mode: Space or Enter to toggle selection
            if (isAdminMode && (e.key === ' ' || e.key === 'Enter') && focusedDrillIndex !== null && filteredDrills[focusedDrillIndex]) {
                e.preventDefault();
                const drill = filteredDrills[focusedDrillIndex];
                const newSet = new Set(selectedDrillIds);
                if (newSet.has(drill.id)) {
                    newSet.delete(drill.id);
                } else {
                    newSet.add(drill.id);
                }
                setSelectedDrillIds(newSet);
                return;
            }

            // Enter to open drill details (if not in admin mode or if no drill is focused)
            if (e.key === 'Enter' && !isAdminMode && focusedDrillIndex !== null && filteredDrills[focusedDrillIndex]) {
                const drill = filteredDrills[focusedDrillIndex];
                const drillElement = document.querySelector(`[data-drill-id="${drill.id}"]`) as HTMLElement;
                // Trigger click on the drill card (which opens video or details)
                drillElement?.click();
                return;
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [
        focusedDrillIndex,
        filteredDrills,
        selectedDrillIds,
        isBulkTagEditorOpen,
        isDeleteConfirmOpen,
        isImportModalOpen,
        genericModalContent,
        isFilterExpanded,
        isViewPanelOpen,
        isAdminMode
    ]);

    // Reset focused index when drills change
    useEffect(() => {
        if (focusedDrillIndex !== null && focusedDrillIndex >= filteredDrills.length) {
            setFocusedDrillIndex(null);
        }
    }, [filteredDrills, focusedDrillIndex]);

    const handleBatchDelete = async () => {
        if (selectedDrillIds.size === 0) return;
        try {
            await drillDb.deleteDrillsBatch(Array.from(selectedDrillIds));
            toast({ title: "Success", description: `${selectedDrillIds.size} drills deleted.` });
            setSelectedDrillIds(new Set());
            setIsDeleteConfirmOpen(false);
        } catch (error) {
            logger.error("Error deleting drills:", error);
            toast({ title: "Error", description: 'Error deleting drills.', variant: "destructive" });
        }
    };

    const handleExportDrills = async () => {
        try {
            // Priority: selected drills > filtered drills > all drills
            let drillsToExport: Drill[];
            if (selectedDrillIds.size > 0) {
                drillsToExport = drills.filter(d => selectedDrillIds.has(d.id));
            } else if (filteredDrills.length > 0) {
                drillsToExport = filteredDrills;
            } else {
                drillsToExport = drills;
            }

            await drillOps.exportToCSV();
            toast({ title: "Success", description: `Exported ${drillsToExport.length} drills to CSV file` });
        } catch (error) {
            logger.error("Error exporting drills:", error);
            toast({ title: "Error", description: 'Error exporting drills', variant: "destructive" });
        }
    };

    const handleImportComplete = (success: number, errors: string[]) => {
        if (success > 0) {
            toast({ title: "Success", description: `Successfully imported ${success} drills` });
        }
        if (errors.length > 0) {
            const errorMessage = errors.length === 1
                ? errors[0]
                : `${errors.length} drills failed to import. Check console for details.`;
            toast({ title: "Error", description: errorMessage, variant: "destructive" });
            logger.error('Import errors:', errors);
        }
    };


    const filterCount = Object.keys(activeFilters).reduce((sum, key) => sum + (activeFilters[key]?.size || 0), 0);

    const mobileHasClearableFilters =
        filterCount > 0 || showFavoritesOnly || Boolean(chatSearchTerm.trim());

    const selectedDrillsArray = useMemo(() =>
        Array.from(selectedDrillIds)
            .map(id => drills.find(d => d.id === id))
            .filter((drill): drill is Drill => Boolean(drill)),
        [selectedDrillIds, drills]);

    // Library statistics
    const libraryStats = useMemo(() => {
        const totalDrills = drills.length;

        // Count by Domain
        const positionalCount = drills.filter(d => getDrillDomain(d) === 'Positional').length;
        const weightRoomCount = drills.filter(d => getDrillDomain(d) === 'Weightroom').length;
        const rollingCount = drills.filter(d => getDrillDomain(d) === 'Rolling').length;
        const locomotionCount = drills.filter(d => getDrillDomain(d) === 'Locomotion').length;
        const breathworkCount = drills.filter(d => getDrillDomain(d) === 'Breathwork').length;
        const plyometricCount = drills.filter(d => getDrillDomain(d) === 'Plyometric').length;

        // Count by Phase
        const phase1Count = drills.filter(d => getTagValues(d, 'Phase').includes('Phase 1')).length;
        const phase2Count = drills.filter(d => getTagValues(d, 'Phase').includes('Phase 2')).length;
        const phase3Count = drills.filter(d => getTagValues(d, 'Phase').includes('Phase 3')).length;

        return {
            totalDrills,
            positionalCount,
            weightRoomCount,
            rollingCount,
            locomotionCount,
            breathworkCount,
            plyometricCount,
            phase1Count,
            phase2Count,
            phase3Count,
        };
    }, [drills]);
    const resultSummary = filteredDrills.length === 0
        ? 'No drills found'
        : `${filteredDrills.length} drill${filteredDrills.length !== 1 ? 's' : ''} found`;

    // Show loading state while data is loading
    if (isLoading) {
        return (
            <div className="flex flex-col gap-8">
                <div className="flex items-center justify-center h-64">
                    <div className="text-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent mx-auto mb-4"></div>
                        <p className="text-muted">Loading drill library...</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-2">
            <AlertDialog
                open={!!pendingContradiction}
                onOpenChange={(open) => {
                    if (!open && !contradictionDecidedRef.current) cancelContradiction();
                }}
            >
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Conflicting posture</AlertDialogTitle>
                        <AlertDialogDescription>
                            {clientFilterName ?? 'This client'} is classified as the opposite
                            orientation. Assigning <strong>{pendingContradiction?.label}</strong> as a
                            need contradicts their current classification (Anterior Orientation and
                            Swayback are mutually exclusive). Assign it anyway?
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel onClick={cancelContradiction}>
                            Keep classification
                        </AlertDialogCancel>
                        <AlertDialogAction onClick={confirmContradiction}>
                            Assign anyway
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {clientFilterName && (
                <div
                    className={`flex flex-wrap items-center justify-between gap-3 rounded-lg border px-3 py-2 text-sm ${
                        editingNeeds
                            ? 'border-warning/50 bg-warning/10'
                            : 'border-primary/30 bg-primary/10'
                    }`}
                    data-testid="drill-library-client-banner"
                >
                    <span className={`font-medium ${editingNeeds ? 'text-warning' : 'text-primary'}`}>
                        {editingNeeds
                            ? `Editing ${clientFilterName}'s needs — changes auto-save`
                            : `Showing drills for ${clientFilterName}'s needs`}
                    </span>
                    <div className="flex items-center gap-1">
                        {editingNeeds ? (
                            <>
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    className="h-7 text-xs"
                                    onClick={revertNeeds}
                                    data-testid="drill-library-needs-revert"
                                >
                                    Revert
                                </Button>
                                <Button
                                    type="button"
                                    variant="default"
                                    size="sm"
                                    className="h-7 text-xs"
                                    onClick={finishEditingNeeds}
                                    data-testid="drill-library-needs-done"
                                >
                                    Done
                                </Button>
                            </>
                        ) : (
                            <>
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    className="h-7 text-xs"
                                    onClick={startEditingNeeds}
                                    data-testid="drill-library-needs-edit"
                                >
                                    Edit needs
                                </Button>
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    className="h-7 text-xs"
                                    onClick={exitClientFilter}
                                    data-testid="drill-library-client-banner-clear"
                                >
                                    Clear
                                </Button>
                            </>
                        )}
                    </div>
                </div>
            )}
            {/* Desktop: stats + filters + toolbar (normal flow; not sticky, so drill cards stay visible while scrolling) */}
            {!isPickMode && (
            <div className="hidden md:block -mx-2 sm:-mx-4 px-2 sm:px-4 space-y-2">
                <div className="flex items-center justify-between gap-3 bg-bg/95 backdrop-blur-md border rounded-lg shadow-sm px-3 py-2">
                    {/* Library Statistics Bar */}
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
                        <div className="flex items-center gap-1.5">
                            <span className="font-semibold text-muted">Library:</span>
                            <span className="font-bold">{libraryStats.totalDrills}</span>
                        </div>
                        <div className="h-3 w-px bg-line" />
                        <div className="flex items-center gap-1.5">
                            <span className="text-muted">Positional:</span>
                            <span
                                className="font-bold text-base"
                                style={{ color: getTagColor('Domain', 'Positional', tagPreferences) }}
                            >
                                {libraryStats.positionalCount}
                            </span>
                        </div>
                        <div className="h-3 w-px bg-line" />
                        <div className="flex items-center gap-1.5">
                            <span className="text-muted">Weightroom:</span>
                            <span
                                className="font-bold text-base"
                                style={{ color: getTagColor('Domain', 'Weightroom', tagPreferences) }}
                            >
                                {libraryStats.weightRoomCount}
                            </span>
                        </div>
                        <div className="h-3 w-px bg-line" />
                        <div className="flex items-center gap-1.5">
                            <span className="text-muted">Rolling:</span>
                            <span
                                className="font-bold text-base"
                                style={{ color: getTagColor('Domain', 'Rolling', tagPreferences) }}
                            >
                                {libraryStats.rollingCount}
                            </span>
                        </div>
                        <div className="h-3 w-px bg-line" />
                        <div className="flex items-center gap-1.5">
                            <span className="text-muted">Phase 1:</span>
                            <span
                                className="font-bold text-base"
                                style={{ color: getTagColor('Phase', 'Phase 1', tagPreferences) }}
                            >
                                {libraryStats.phase1Count}
                            </span>
                        </div>
                        <div className="flex items-center gap-1.5">
                            <span className="text-muted">Phase 2:</span>
                            <span
                                className="font-bold text-base"
                                style={{ color: getTagColor('Phase', 'Phase 2', tagPreferences) }}
                            >
                                {libraryStats.phase2Count}
                            </span>
                        </div>
                        <div className="flex items-center gap-1.5">
                            <span className="text-muted">Phase 3:</span>
                            <span
                                className="font-bold text-base"
                                style={{ color: getTagColor('Phase', 'Phase 3', tagPreferences) }}
                            >
                                {libraryStats.phase3Count}
                            </span>
                        </div>
                    </div>
                    <DomainCycleButton
                        selectedDomains={activeFilters['Domain']}
                        onCycle={cycleLibraryDomain}
                        className="flex-shrink-0"
                    />
                </div>
                {/* Inline drawer for search and filters */}
                <div
                    id="library-options-drawer"
                    className={`${isOptionsDrawerOpen ? 'block' : 'hidden'} md:block`}
                >
                    <div className="grid gap-2 md:gap-4 bg-surface/20 border rounded-t-lg px-3 py-3 [grid-template-columns:repeat(auto-fill,minmax(14rem,1fr))]">
                        {/* Phase Filter */}
                        <MultiSelect
                            placeholder="All Phases"
                            options={getMainFilterOptions('Phase', null)}
                            selectedValues={Array.from(activeFilters['Phase'] || [])}
                            onChange={(values) => {
                                setActiveFilters(prev => {
                                    const next = { ...prev };
                                    if (values.length === 0) {
                                        delete next['Phase'];
                                    } else {
                                        next['Phase'] = new Set(values);
                                    }
                                    return next;
                                });
                            }}
                        />
                        {/* Domain */}
                        <MultiSelect
                            placeholder={getCategoryDisplayLabel('Domain', taxonomy)}
                            options={getMainFilterOptions('Domain', null)}
                            selectedValues={Array.from(activeFilters['Domain'] || [])}
                            onChange={(values) => {
                                setActiveFilters(prev => {
                                    const next = { ...prev };
                                    if (values.length === 0) {
                                        delete next['Domain'];
                                    } else {
                                        next['Domain'] = new Set(values);
                                    }
                                    return next;
                                });
                            }}
                        />
                        {/* 2. Posture - displayLabel from taxonomy */}
                        <MultiSelect
                            placeholder={getCategoryDisplayLabel('Posture', taxonomy)}
                            options={getMainFilterOptions('Posture', null)}
                            selectedValues={Array.from(activeFilters['Posture'] || [])}
                            onChange={(values) => {
                                setActiveFilters(prev => {
                                    const next = { ...prev };
                                    if (values.length === 0) {
                                        delete next['Posture'];
                                    } else {
                                        next['Posture'] = new Set(values);
                                    }
                                    return next;
                                });
                            }}
                        />
                        {/* 3. Movement Limitation */}
                        <MultiSelect
                            placeholder="Movement Limitation"
                            options={getMainFilterOptions('Movement Limitation', null)}
                            selectedValues={Array.from(activeFilters['Movement Limitation'] || [])}
                            onChange={(values) => {
                                setActiveFilters(prev => {
                                    const next = { ...prev };
                                    if (values.length === 0) {
                                        delete next['Movement Limitation'];
                                    } else {
                                        next['Movement Limitation'] = new Set(values);
                                    }
                                    return next;
                                });
                            }}
                        />
                        {/* 4. Biomechanical Intent - full taxonomy */}
                        <MultiSelect
                            placeholder="Biomechanical Intent"
                            options={getMainFilterOptions('Biomechanical Intent', null)}
                            selectedValues={Array.from(activeFilters['Biomechanical Intent'] || [])}
                            onChange={(values) => {
                                setActiveFilters(prev => {
                                    const next = { ...prev };
                                    if (values.length === 0) {
                                        delete next['Biomechanical Intent'];
                                    } else {
                                        next['Biomechanical Intent'] = new Set(values);
                                    }
                                    return next;
                                });
                            }}
                        />
                        {/* Search — full width below filter row */}
                        <Input
                            ref={searchInputRef}
                            type="text"
                            placeholder="Search drills... (Press '/' to focus)"
                            value={chatSearchTerm}
                            onChange={(e) => setChatSearchTerm(e.target.value)}
                            className="h-11 md:h-12 w-full min-w-0 col-span-full text-base"
                        />
                    </div>
                </div>

                {/* Desktop Contextual Toolbar */}
                <div className="bg-surface/30 backdrop-blur-sm shadow-sm border rounded-lg supports-[backdrop-filter]:bg-surface/30">
                    <div className="flex items-center justify-end gap-2 px-4 py-3 md:px-6">
                        <div className="flex items-center gap-2 w-full whitespace-nowrap overflow-x-auto touch-pan-xy justify-between">
                            <div className="flex items-center gap-2">
                                {/* Sorting dropdown */}
                                <Select value={sortOption} onValueChange={(value: any) => setSortOption(value)}>
                                    <SelectTrigger className="w-40 sm:w-48 h-10">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="date-desc">Newest First</SelectItem>
                                        <SelectItem value="date-asc">Oldest First</SelectItem>
                                        <SelectItem value="name-asc">Name (A-Z)</SelectItem>
                                        <SelectItem value="name-desc">Name (Z-A)</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="flex items-center gap-2">
                                {/* Favorites */}
                                <Button
                                    onClick={() => setShowFavoritesOnly(!showFavoritesOnly)}
                                    variant={showFavoritesOnly ? "default" : "outline"}
                                    size="sm"
                                    className="h-10 w-10 sm:w-auto sm:px-3 p-0 flex items-center justify-center"
                                >
                                    <Heart className={`h-4 w-4 ${isMobile ? '' : 'sm:mr-2'} ${showFavoritesOnly ? 'fill-current' : ''}`} />
                                    <span className="hidden sm:inline">
                                        {showFavoritesOnly ? 'Showing Favorites' : 'Favorites'}
                                    </span>
                                    {!showFavoritesOnly && favoriteDrills.length > 0 && (
                                        <span className="ml-1 text-xs bg-accent/20 text-accent px-2 py-0.5 rounded-full hidden sm:inline">
                                            {favoriteDrills.length}
                                        </span>
                                    )}
                                </Button>
                                {/* Filters */}
                                <Button
                                    onClick={() => handleFilterExpandedToggle(!isFilterExpanded)}
                                    variant="outline"
                                    size="sm"
                                    className="h-10 relative"
                                >
                                    <Filter className="h-4 w-4 sm:mr-2" />
                                    <span className="hidden sm:inline">
                                        {isFilterExpanded ? 'Hide' : 'Filters'}
                                    </span>
                                    {filterCount > 0 && (
                                        <span className="absolute -top-1 -right-1 bg-accent text-accent-fg text-xs rounded-full h-5 w-5 flex items-center justify-center">
                                            {filterCount}
                                        </span>
                                    )}
                                </Button>
                                {/* Reset if anything applied */}
                                {(filterCount > 0 || showFavoritesOnly || chatSearchTerm) && (
                                    <Button
                                        onClick={resetFilters}
                                        variant="outline"
                                        size="sm"
                                        className="h-10"
                                    >
                                        <RotateCcw className="h-4 w-4 sm:mr-2" />
                                        <span className="hidden sm:inline">Reset</span>
                                    </Button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            )}

            {/* Pick mode (drill plan slot): search + sort only */}
            {isMobile && isPickMode && (
                <div className="space-y-2 pb-1">
                    <Input
                        ref={searchInputRef}
                        type="text"
                        placeholder="Search drills..."
                        value={chatSearchTerm}
                        onChange={(e) => setChatSearchTerm(e.target.value)}
                        className="h-11 w-full text-base"
                    />
                    <div className="flex items-center gap-2">
                        <Select value={sortOption} onValueChange={(value: any) => setSortOption(value)}>
                            <SelectTrigger className="h-10 flex-1 min-w-0">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="date-desc">Newest First</SelectItem>
                                <SelectItem value="date-asc">Oldest First</SelectItem>
                                <SelectItem value="name-asc">Name (A-Z)</SelectItem>
                                <SelectItem value="name-desc">Name (Z-A)</SelectItem>
                            </SelectContent>
                        </Select>
                        <Button
                            onClick={() => setShowFavoritesOnly(!showFavoritesOnly)}
                            variant={showFavoritesOnly ? 'default' : 'outline'}
                            size="sm"
                            className="h-10 w-10 p-0 shrink-0"
                            aria-label="Favorites only"
                        >
                            <Heart className={`h-4 w-4 ${showFavoritesOnly ? 'fill-current' : ''}`} />
                        </Button>
                    </div>
                </div>
            )}

            {/* Mobile: Search Box */}
            {isMobile && !isPickMode && (
                <div className="pb-2 space-y-2">
                    <Button
                        type="button"
                        onClick={resetFilters}
                        variant="outline"
                        size="sm"
                        disabled={!mobileHasClearableFilters}
                        className="w-full h-10 justify-center gap-2"
                    >
                        <RotateCcw className="h-4 w-4 shrink-0" />
                        Clear all filters
                    </Button>
                    <div className="grid grid-cols-1 gap-2 bg-surface/20 border rounded-lg px-3 py-3">
                        <DomainCycleButton
                            selectedDomains={activeFilters['Domain']}
                            onCycle={cycleLibraryDomain}
                            className="w-full justify-center"
                            testId="drill-library-domain-cycle-mobile"
                        />
                        {/* 1. Phase */}
                        <MultiSelect
                            placeholder="Phase"
                            options={getMainFilterOptions('Phase', null)}
                            selectedValues={Array.from(activeFilters['Phase'] || [])}
                            onChange={(values) => {
                                setActiveFilters(prev => {
                                    const next = { ...prev };
                                    if (values.length === 0) {
                                        delete next['Phase'];
                                    } else {
                                        next['Phase'] = new Set(values);
                                    }
                                    return next;
                                });
                            }}
                        />
                        {/* Domain */}
                        <MultiSelect
                            placeholder={getCategoryDisplayLabel('Domain', taxonomy)}
                            options={getMainFilterOptions('Domain', null)}
                            selectedValues={Array.from(activeFilters['Domain'] || [])}
                            onChange={(values) => {
                                setActiveFilters(prev => {
                                    const next = { ...prev };
                                    if (values.length === 0) {
                                        delete next['Domain'];
                                    } else {
                                        next['Domain'] = new Set(values);
                                    }
                                    return next;
                                });
                            }}
                        />
                        {/* 2. Posture - displayLabel from taxonomy */}
                        <MultiSelect
                            placeholder={getCategoryDisplayLabel('Posture', taxonomy)}
                            options={getMainFilterOptions('Posture', null)}
                            selectedValues={Array.from(activeFilters['Posture'] || [])}
                            onChange={(values) => {
                                setActiveFilters(prev => {
                                    const next = { ...prev };
                                    if (values.length === 0) {
                                        delete next['Posture'];
                                    } else {
                                        next['Posture'] = new Set(values);
                                    }
                                    return next;
                                });
                            }}
                        />
                        {/* 3. Movement Limitation */}
                        <MultiSelect
                            placeholder="Movement Limitation"
                            options={getMainFilterOptions('Movement Limitation', null)}
                            selectedValues={Array.from(activeFilters['Movement Limitation'] || [])}
                            onChange={(values) => {
                                setActiveFilters(prev => {
                                    const next = { ...prev };
                                    if (values.length === 0) {
                                        delete next['Movement Limitation'];
                                    } else {
                                        next['Movement Limitation'] = new Set(values);
                                    }
                                    return next;
                                });
                            }}
                        />
                        {/* 4. Biomechanical Intent - full taxonomy */}
                        <MultiSelect
                            placeholder="Biomechanical Intent"
                            options={getMainFilterOptions('Biomechanical Intent', null)}
                            selectedValues={Array.from(activeFilters['Biomechanical Intent'] || [])}
                            onChange={(values) => {
                                setActiveFilters(prev => {
                                    const next = { ...prev };
                                    if (values.length === 0) {
                                        delete next['Biomechanical Intent'];
                                    } else {
                                        next['Biomechanical Intent'] = new Set(values);
                                    }
                                    return next;
                                });
                            }}
                            optionLabels={MAIN_BIOMECHANICAL_LABELS}
                        />
                        {/* Search — last in list */}
                        <Input
                            ref={searchInputRef}
                            type="text"
                            placeholder="Search drills... (Press '/' to focus)"
                            value={chatSearchTerm}
                            onChange={(e) => setChatSearchTerm(e.target.value)}
                            className="h-12 w-full min-h-12 bg-bg text-base"
                        />
                    </div>
                    <Button
                        type="button"
                        onClick={resetFilters}
                        variant="outline"
                        size="sm"
                        disabled={!mobileHasClearableFilters}
                        className="w-full h-10 justify-center gap-2"
                    >
                        <RotateCcw className="h-4 w-4 shrink-0" />
                        Clear all filters
                    </Button>
                </div>
            )}

            {/* Mobile: Sorting and Quick Filter Toolbar */}
            {isMobile && !isPickMode && (
                <div className="bg-surface/30 backdrop-blur-sm shadow-sm border rounded-lg supports-[backdrop-filter]:bg-surface/30">
                    <div className="flex items-center justify-between gap-2 px-4 py-3">
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                            {/* Sorting dropdown */}
                            <Select value={sortOption} onValueChange={(value: any) => setSortOption(value)}>
                                <SelectTrigger className="w-40 h-10 flex-shrink-0">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="date-desc">Newest First</SelectItem>
                                    <SelectItem value="date-asc">Oldest First</SelectItem>
                                    <SelectItem value="name-asc">Name (A-Z)</SelectItem>
                                    <SelectItem value="name-desc">Name (Z-A)</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                            {/* Favorites - fixed width on mobile to match other buttons */}
                            <Button
                                onClick={() => setShowFavoritesOnly(!showFavoritesOnly)}
                                variant={showFavoritesOnly ? "default" : "outline"}
                                size="sm"
                                className="h-10 w-10 p-0 flex items-center justify-center"
                            >
                                <Heart className={`h-4 w-4 ${showFavoritesOnly ? 'fill-current' : ''}`} />
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {/* Mobile: Inline drawer for search and filters */}
            {isMobile && !isPickMode && (
                <div
                    id="library-options-drawer"
                    className={`${isOptionsDrawerOpen ? 'block' : 'hidden'} space-y-2`}
                >
                    <Button
                        type="button"
                        onClick={resetFilters}
                        variant="outline"
                        size="sm"
                        disabled={!mobileHasClearableFilters}
                        className="w-full h-10 justify-center gap-2"
                    >
                        <RotateCcw className="h-4 w-4 shrink-0" />
                        Clear all filters
                    </Button>
                    <div className="grid grid-cols-1 gap-2 bg-surface/20 border rounded-lg px-3 py-3">
                        {/* Phase Filter */}
                        <MultiSelect
                            placeholder="All Phases"
                            options={getMainFilterOptions('Phase', null)}
                            selectedValues={Array.from(activeFilters['Phase'] || [])}
                            onChange={(values) => {
                                setActiveFilters(prev => {
                                    const next = { ...prev };
                                    if (values.length === 0) {
                                        delete next['Phase'];
                                    } else {
                                        next['Phase'] = new Set(values);
                                    }
                                    return next;
                                });
                            }}
                        />
                        {/* Domain */}
                        <MultiSelect
                            placeholder={getCategoryDisplayLabel('Domain', taxonomy)}
                            options={getMainFilterOptions('Domain', null)}
                            selectedValues={Array.from(activeFilters['Domain'] || [])}
                            onChange={(values) => {
                                setActiveFilters(prev => {
                                    const next = { ...prev };
                                    if (values.length === 0) {
                                        delete next['Domain'];
                                    } else {
                                        next['Domain'] = new Set(values);
                                    }
                                    return next;
                                });
                            }}
                        />
                        {/* 2. Posture - displayLabel from taxonomy */}
                        <MultiSelect
                            placeholder={getCategoryDisplayLabel('Posture', taxonomy)}
                            options={getMainFilterOptions('Posture', null)}
                            selectedValues={Array.from(activeFilters['Posture'] || [])}
                            onChange={(values) => {
                                setActiveFilters(prev => {
                                    const next = { ...prev };
                                    if (values.length === 0) {
                                        delete next['Posture'];
                                    } else {
                                        next['Posture'] = new Set(values);
                                    }
                                    return next;
                                });
                            }}
                        />
                        {/* 3. Movement Limitation */}
                        <MultiSelect
                            placeholder="Movement Limitation"
                            options={getMainFilterOptions('Movement Limitation', null)}
                            selectedValues={Array.from(activeFilters['Movement Limitation'] || [])}
                            onChange={(values) => {
                                setActiveFilters(prev => {
                                    const next = { ...prev };
                                    if (values.length === 0) {
                                        delete next['Movement Limitation'];
                                    } else {
                                        next['Movement Limitation'] = new Set(values);
                                    }
                                    return next;
                                });
                            }}
                        />
                        {/* 4. Biomechanical Intent - full taxonomy */}
                        <MultiSelect
                            placeholder="Biomechanical Intent"
                            options={getMainFilterOptions('Biomechanical Intent', null)}
                            selectedValues={Array.from(activeFilters['Biomechanical Intent'] || [])}
                            onChange={(values) => {
                                setActiveFilters(prev => {
                                    const next = { ...prev };
                                    if (values.length === 0) {
                                        delete next['Biomechanical Intent'];
                                    } else {
                                        next['Biomechanical Intent'] = new Set(values);
                                    }
                                    return next;
                                });
                            }}
                        />
                        <Input
                            type="text"
                            placeholder="Search drills... (Press '/' to focus)"
                            value={chatSearchTerm}
                            onChange={(e) => setChatSearchTerm(e.target.value)}
                            className="h-12 w-full min-h-12 bg-bg text-base"
                        />
                    </div>
                    <Button
                        type="button"
                        onClick={resetFilters}
                        variant="outline"
                        size="sm"
                        disabled={!mobileHasClearableFilters}
                        className="w-full h-10 justify-center gap-2"
                    >
                        <RotateCcw className="h-4 w-4 shrink-0" />
                        Clear all filters
                    </Button>
                </div>
            )}

            {/* Contextual Toolbar - Admin and Selection Controls */}
            {isAdminMode && !isPickMode && (
                <ContextualToolbar position="top">
                    <ToolbarButtonGroup align="between" className="w-full">
                        {/* Selection Controls */}
                        <div className="flex items-center gap-2 flex-wrap">
                            {selectedDrillIds.size > 0 && (
                                <>
                                    <Button
                                        onClick={() => {
                                            setIsBulkEditPopinExpanded(false);
                                            setIsBulkTagEditorOpen(false);
                                            setSelectedDrillIds(new Set());
                                        }}
                                        variant="outline"
                                        size="sm"
                                        className="h-10"
                                    >
                                        Cancel Selection
                                        <span className="ml-2 bg-accent text-accent-fg text-xs font-semibold px-2 py-0.5 rounded-full">
                                            {selectedDrillIds.size}
                                        </span>
                                    </Button>
                                    <Button
                                        onClick={() => {
                                            const allFilteredIds = new Set(filteredDrills.map(d => d.id));
                                            setSelectedDrillIds(allFilteredIds);
                                        }}
                                        variant="outline"
                                        size="sm"
                                        className="h-10"
                                    >
                                        Select All
                                        {selectedDrillIds.size < filteredDrills.length && (
                                            <span className="ml-2 text-xs text-muted">
                                                ({filteredDrills.length - selectedDrillIds.size} remaining)
                                            </span>
                                        )}
                                    </Button>
                                    <Button
                                        onClick={() => setIsBulkTagEditorOpen(true)}
                                        variant="outline"
                                        size="sm"
                                        className="h-10"
                                    >
                                        <Tag className="h-4 w-4 sm:mr-2" />
                                        <span className="hidden sm:inline">Tags</span>
                                        <span className="ml-2 bg-accent text-accent-fg text-xs font-semibold px-2 py-0.5 rounded-full">
                                            {selectedDrillIds.size}
                                        </span>
                                    </Button>
                                </>
                            )}
                            {selectedDrillIds.size === 0 && (
                                <Button
                                    onClick={() => {
                                        const allFilteredIds = new Set(filteredDrills.map(d => d.id));
                                        setSelectedDrillIds(allFilteredIds);
                                    }}
                                    variant="outline"
                                    size="sm"
                                    className="h-10"
                                >
                                    Select All
                                    <span className="ml-2 text-xs text-muted">
                                        ({filteredDrills.length} drills)
                                    </span>
                                </Button>
                            )}
                        </div>

                        {/* Primary Actions */}
                        <div className="flex items-center gap-2 flex-wrap justify-end">
                            <Button
                                onClick={handleExportDrills}
                                variant="outline"
                                size="sm"
                                className="h-10"
                            >
                                <span className="sm:mr-2"><DownloadIcon /></span>
                                <span className="hidden sm:inline">Export</span>
                            </Button>
                            <Button
                                onClick={() => setIsImportModalOpen(true)}
                                variant="outline"
                                size="sm"
                                className="h-10"
                            >
                                <span className="sm:mr-2"><UploadIcon /></span>
                                <span className="hidden sm:inline">Import</span>
                            </Button>
                        </div>
                    </ToolbarButtonGroup>
                </ContextualToolbar>
            )}

            {/* View Panel Drawer - Layout Options and Colorization */}
            {isViewPanelOpen && (
                <div
                    className="relative mb-4 bg-surface border rounded-lg shadow-sm"
                >
                    <div className="p-4 border-b flex items-center justify-between">
                        <h3 className="font-semibold text-sm">View Options</h3>
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleViewPanelToggle(false)}
                            className="h-8 w-8"
                        >
                            <X className="h-4 w-4" />
                        </Button>
                    </div>

                    <div className="p-4 space-y-4">
                        {/* Colorization Selector */}
                        <div className="space-y-2">
                            <Label className="text-sm font-medium">Colorize Cards By</Label>
                            <Select
                                value={colorizationCategory}
                                onValueChange={setColorizationCategory}
                            >
                                <SelectTrigger className="h-10">
                                    <SelectValue placeholder="Select category" />
                                </SelectTrigger>
                                <SelectContent>
                                    {availableCategories.map((category) => (
                                        <SelectItem key={category} value={category}>
                                            {category}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            <p className="text-xs text-muted">
                                Drill cards will be colored based on their {colorizationCategory} tag
                            </p>
                        </div>

                        {/* Layout Mode Options - Desktop only */}
                        {!isMobile && (
                            <>
                                <Separator />

                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <Label className="text-xs mb-2 block">Layout Mode</Label>
                                        <div className="flex gap-2">
                                            <Button
                                                size="sm"
                                                variant={drillLayoutMode === 'grid' ? 'default' : 'outline'}
                                                onClick={() => setSettings(prev => ({ ...prev, drillLayoutMode: 'grid' }))}
                                                className="h-9 flex-1"
                                            >
                                                <Grid3x3 className="h-4 w-4 mr-2" />
                                                Grid
                                            </Button>
                                            <Button
                                                size="sm"
                                                variant={drillLayoutMode === 'list' ? 'default' : 'outline'}
                                                onClick={() => setSettings(prev => ({ ...prev, drillLayoutMode: 'list' }))}
                                                className="h-9 flex-1"
                                            >
                                                List
                                            </Button>
                                        </div>
                                    </div>
                                    {drillLayoutMode === 'grid' && (
                                        <div>
                                            <Label className="text-xs mb-2 block">Grid Columns</Label>
                                            <Select
                                                value={drillGridColumns.toString()}
                                                onValueChange={(value) => setSettings(prev => ({ ...prev, drillGridColumns: parseInt(value) }))}
                                            >
                                                <SelectTrigger className="h-9">
                                                    <SelectValue />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="2">2 Columns</SelectItem>
                                                    <SelectItem value="3">3 Columns</SelectItem>
                                                    <SelectItem value="4">4 Columns</SelectItem>
                                                    <SelectItem value="5">5 Columns</SelectItem>
                                                </SelectContent>
                                            </Select>
                                        </div>
                                    )}
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}

            {/* Filter Expansion Panel - advanced filters only (no duplicate of default Search/Phase/Posture/etc) */}
            {isFilterExpanded && (
                <div className="p-6 bg-surface/50 rounded-lg border space-y-6">
                    {Object.keys(usedTaxonomy).length > 0 ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                            {Object.entries(usedTaxonomy)
                                .sort(([a], [b]) => a.localeCompare(b, undefined, { sensitivity: 'base' }))
                                .map(([category, config]) => {
                                    if (config.type === 'text') return null;
                                    if (MAIN_FILTER_CATEGORY_KEYS.has(category)) return null;
                                    return (
                                        <div key={category}>
                                            <h4 className="font-semibold mb-3 text-sm text-fg/80">{category}</h4>
                                            <div className="flex flex-wrap gap-1.5">
                                                {[...config.options]
                                                    .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }))
                                                    .map(opt => (
                                                    <UnifiedTag
                                                        key={opt}
                                                        category={category}
                                                        value={opt}
                                                        onClick={() => {
                                                            setActiveFilters(prev => {
                                                                const newCategoryFilters = new Set(prev[category] || []);
                                                                if (newCategoryFilters.has(opt)) {
                                                                    newCategoryFilters.delete(opt);
                                                                } else {
                                                                    newCategoryFilters.add(opt);
                                                                }
                                                                if (newCategoryFilters.size === 0) {
                                                                    const newFilters: any = { ...prev };
                                                                    delete newFilters[category];
                                                                    return newFilters;
                                                                }
                                                                return { ...prev, [category]: newCategoryFilters } as any;
                                                            });
                                                        }}
                                                        isActive={activeFilters[category]?.has(opt) || false}
                                                        size="sm"
                                                    />
                                                ))}
                                            </div>
                                        </div>
                                    );
                                })}
                        </div>
                    ) : (
                        <div className="text-center py-12 text-muted">
                            <p className="text-sm">No filter categories available</p>
                            <p className="text-xs">Add drills with tags to see filter options here.</p>
                        </div>
                    )}

                    <div className="flex justify-between items-center gap-3 mt-6 pt-4 border-t">
                        <div className="text-sm text-muted">Filters are applied as you select them</div>
                        <Button
                            onClick={() => { setActiveFilters({}); }}
                            variant="outline"
                        >
                            Clear All
                        </Button>
                    </div>
                </div>
            )}

            {/* Legacy controls block (hidden) */}
            <div className="space-y-4 mb-6 hidden">
                {/* Primary Filter Row */}
                <div className="grid gap-4 [grid-template-columns:repeat(auto-fill,minmax(14rem,1fr))]">
                    {/* Phase Filter */}
                    <MultiSelect
                        placeholder="All Phases"
                        options={getMainFilterOptions('Phase', null)}
                        selectedValues={Array.from(activeFilters['Phase'] || [])}
                        onChange={(values) => {
                            setActiveFilters(prev => {
                                const next = { ...prev };
                                if (values.length === 0) {
                                    delete next['Phase'];
                                } else {
                                    next['Phase'] = new Set(values);
                                }
                                return next;
                            });
                        }}
                    />
                    {/* Domain */}
                    <MultiSelect
                        placeholder={getCategoryDisplayLabel('Domain', taxonomy)}
                        options={getMainFilterOptions('Domain', null)}
                        selectedValues={Array.from(activeFilters['Domain'] || [])}
                        onChange={(values) => {
                            setActiveFilters(prev => {
                                const next = { ...prev };
                                if (values.length === 0) {
                                    delete next['Domain'];
                                } else {
                                    next['Domain'] = new Set(values);
                                }
                                return next;
                            });
                        }}
                    />
                    {/* 2. Posture - displayLabel from taxonomy */}
                    <MultiSelect
                        placeholder={getCategoryDisplayLabel('Posture', taxonomy)}
                        options={getMainFilterOptions('Posture', null)}
                        selectedValues={Array.from(activeFilters['Posture'] || [])}
                        onChange={(values) => {
                            setActiveFilters(prev => {
                                const next = { ...prev };
                                if (values.length === 0) {
                                    delete next['Posture'];
                                } else {
                                    next['Posture'] = new Set(values);
                                }
                                return next;
                            });
                        }}
                    />
                    {/* 3. Movement Limitation */}
                    <MultiSelect
                        placeholder="Movement Limitation"
                        options={getMainFilterOptions('Movement Limitation', null)}
                        selectedValues={Array.from(activeFilters['Movement Limitation'] || [])}
                        onChange={(values) => {
                            setActiveFilters(prev => {
                                const next = { ...prev };
                                if (values.length === 0) {
                                    delete next['Movement Limitation'];
                                } else {
                                    next['Movement Limitation'] = new Set(values);
                                }
                                return next;
                            });
                        }}
                    />
                    {/* 4. Biomechanical Intent - full taxonomy */}
                    <MultiSelect
                        placeholder="Biomechanical Intent"
                        options={getMainFilterOptions('Biomechanical Intent', null)}
                        selectedValues={Array.from(activeFilters['Biomechanical Intent'] || [])}
                        onChange={(values) => {
                            setActiveFilters(prev => {
                                const next = { ...prev };
                                if (values.length === 0) {
                                    delete next['Biomechanical Intent'];
                                } else {
                                    next['Biomechanical Intent'] = new Set(values);
                                }
                                return next;
                            });
                        }}
                    />
                    <div className="col-span-full">
                        <Input
                            type="text"
                            placeholder="Search drills..."
                            value={chatSearchTerm}
                            onChange={(e) => setChatSearchTerm(e.target.value)}
                            className="h-12 w-full text-base"
                        />
                    </div>
                </div>

                {/* Admin Controls */}
                {isAdminMode && selectedDrillIds.size > 0 && (
                    <div className="space-y-4">
                        <div className="flex flex-wrap items-center gap-3 p-4 bg-bg rounded-lg border">
                            <Button
                                onClick={() => setIsBulkEditPopinExpanded(true)}
                                variant="outline"
                                disabled={selectedDrillIds.size === 0}
                            >
                                Edit Tags
                            </Button>
                            <Button
                                onClick={() => setIsDeleteConfirmOpen(true)}
                                variant="destructive"
                            >
                                Delete Selected
                            </Button>
                        </div>

                        {isBulkEditPopinExpanded && (
                            <BulkEditTagsPopin
                                isExpanded={isBulkEditPopinExpanded}
                                onClose={() => setIsBulkEditPopinExpanded(false)}
                                onApply={async (changes) => {
                                    if (selectedDrillsArray.length === 0) {
                                        toast({
                                            title: "No drills selected",
                                            description: "Choose one or more drills before editing tags.",
                                            variant: "destructive"
                                        });
                                        return;
                                    }

                                    try {
                                        for (const drill of selectedDrillsArray) {
                                            const updatedTags = { ...drill.tags };

                                            if (changes.add) {
                                                const current = updatedTags[changes.add.category];
                                                if (Array.isArray(current)) {
                                                    if (!current.includes(changes.add.value)) {
                                                        updatedTags[changes.add.category] = [...current, changes.add.value];
                                                    }
                                                } else if (typeof current === 'string') {
                                                    updatedTags[changes.add.category] = current === changes.add.value
                                                        ? current
                                                        : [current, changes.add.value];
                                                } else {
                                                    updatedTags[changes.add.category] = changes.add.value;
                                                }
                                            }

                                            if (changes.remove.length > 0) {
                                                changes.remove.forEach(({ category, value }) => {
                                                    const current = updatedTags[category];
                                                    if (Array.isArray(current)) {
                                                        const filtered = current.filter(v => v !== value);
                                                        if (filtered.length === 0) {
                                                            delete updatedTags[category];
                                                        } else if (filtered.length === 1) {
                                                            updatedTags[category] = filtered[0];
                                                        } else {
                                                            updatedTags[category] = filtered;
                                                        }
                                                    } else if (current === value) {
                                                        delete updatedTags[category];
                                                    }
                                                });
                                            }

                                            await drillDb.updateDrill(drill.id, { tags: updatedTags });
                                        }

                                        toast({
                                            title: "Tags updated",
                                            description: `Applied changes to ${selectedDrillsArray.length} drill${selectedDrillsArray.length === 1 ? '' : 's'}.`
                                        });
                                        setIsBulkEditPopinExpanded(false);
                                    } catch (error) {
                                        logger.error('Error updating tags in bulk:', error);
                                        toast({
                                            title: "Failed to update tags",
                                            description: error instanceof Error ? error.message : 'Unknown error occurred.',
                                            variant: "destructive"
                                        });
                                    }
                                }}
                                selectedDrills={selectedDrillsArray}
                            />
                        )}
                    </div>
                )}

            </div>

            {/* Active Filters Display - hidden on mobile; stacks + wrapped tag rows when viewport/content is narrow */}
            {!isMobile && filterCount > 0 && (
                <div className="flex flex-col gap-3 p-4 bg-accent/5 border border-accent/20 rounded-lg xl:flex-row xl:items-start xl:gap-4">
                    <div className="flex items-center gap-2 shrink-0">
                        <Filter className="w-4 h-4 text-accent" />
                        <span className="text-sm font-medium">Active Filters:</span>
                    </div>
                    <div className="flex flex-wrap content-start gap-1.5 w-full min-w-0 xl:flex-1">
                        {Object.entries(activeFilters).flatMap(([category, values]) =>
                            Array.from(values).map(value => (
                                <UnifiedTag
                                    key={`${category}-${value}`}
                                    category={category}
                                    value={value}
                                    onClick={() => {
                                        setActiveFilters(prev => {
                                            const newFilters = { ...prev };
                                            const categorySet = new Set(newFilters[category]);
                                            categorySet.delete(value);
                                            if (categorySet.size === 0) {
                                                delete newFilters[category];
                                            } else {
                                                newFilters[category] = categorySet;
                                            }
                                            return newFilters;
                                        });
                                    }}
                                    size="sm"
                                />
                            ))
                        )}
                    </div>
                    <div className="flex flex-wrap items-center justify-between gap-3 w-full shrink-0 xl:w-auto xl:justify-end xl:gap-4">
                        <div className="flex flex-col gap-1 text-left sm:text-right min-w-0">
                            <span className="text-xs uppercase tracking-wide text-muted/70">Results</span>
                            <p className="text-sm font-semibold text-fg">
                                {resultSummary}
                                {chatSearchResults && filteredDrills.length > 0 && (
                                    <span className="ml-2 text-xs text-accent">
                                        of {drills.length} total drills
                                    </span>
                                )}
                            </p>
                            <span className="text-xs text-muted">
                                {filteredDrills.length > 0 && filterCount > 0
                                    ? `Filtered by ${filterCount} filter${filterCount === 1 ? '' : 's'}`
                                    : filteredDrills.length > 0
                                        ? 'No filters applied'
                                        : ''}
                            </span>
                        </div>

                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={resetFilters}
                            className="h-8 shrink-0"
                        >
                            <X className="w-4 h-4 mr-1" />
                            Clear All
                        </Button>
                    </div>
                </div>
            )}

            {/* Drill Cards Grid */}
            <div
                className={
                    drillLayoutMode === 'list'
                        ? 'flex flex-col gap-4 w-full'
                        : `grid gap-6 ${gridColumnClasses[drillGridColumns] || gridColumnClasses[3]} mx-auto w-full`
                }
            >
                {filteredDrills.map((drill, index) => {
                    return (
                        <div
                            key={`${drill.id}-${colorizationCategory}`}
                            data-drill-id={drill.id}
                            className={`animate-in fade-in slide-in-from-bottom-4 duration-500 ${drillLayoutMode === 'list'
                                ? 'w-full'
                                : 'w-full max-w-md mx-auto'
                                } ${focusedDrillIndex === index ? 'ring-2 ring-accent ring-offset-2' : ''}`}
                            style={{ animationDelay: `${Math.min(index * 50, 500)}ms` }}
                        >
                            <DrillCard
                                drill={drill}
                                selectionMode={effectiveSelectionMode}
                                isSelected={selectedDrillIds.has(drill.id)}
                                onSelectionChange={(id, isSelected) => {
                                    const newSet = new Set(selectedDrillIds);
                                    if (isSelected) newSet.add(id);
                                    else newSet.delete(id);
                                    setSelectedDrillIds(newSet);
                                }}
                                setGenericModalContent={setGenericModalContent}
                                onUpdateDrill={(id, data) => drillDb.updateDrill(id, data)}
                                onDeleteDrill={(id) => drillDb.deleteDrill(id)}
                                allDrills={drills}
                                setActiveFilters={setActiveFilters}
                                tagPreferences={tagPreferences}
                                activeFilters={activeFilters}
                                isAdminMode={isAdminMode}
                                isFavorite={favoriteDrills.includes(drill.id)}
                                onToggleFavorite={toggleFavorite}
                                colorizationCategory={colorizationCategory}
                                layoutMode={drillLayoutMode}
                                cardPreferences={drillCardPreferences}
                                onPickDrill={onPickDrill}
                            />
                        </div>
                    )
                })}
            </div>

            {filteredDrills.length === 0 && (
                <div className="text-center py-12 text-muted">
                    <p className="text-sm">Try adjusting your search or filters</p>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            <Modal isOpen={isDeleteConfirmOpen} onClose={() => setIsDeleteConfirmOpen(false)} title="Confirm Deletion">
                <p>Are you sure you want to delete {selectedDrillIds.size} drill(s)? This action cannot be undone.</p>
                <div className="flex justify-end gap-3 mt-4">
                    <Button variant="outline" onClick={() => setIsDeleteConfirmOpen(false)}>
                        Cancel
                    </Button>
                    <Button variant="destructive" onClick={handleBatchDelete}>
                        Delete
                    </Button>
                </div>
            </Modal>

            {/* Generic Modal */}
            {genericModalContent && (
                <Modal
                    isOpen={!!genericModalContent}
                    onClose={() => setGenericModalContent(null)}
                    title={genericModalContent.title}
                    maxWidth={genericModalContent.maxWidth}
                    padding={genericModalContent.padding}
                >
                    {genericModalContent.body}
                </Modal>
            )}

            {/* Import Modal */}
            <DrillImportModal
                isOpen={isImportModalOpen}
                onClose={() => setIsImportModalOpen(false)}
                existingDrills={drills}
                onImportComplete={handleImportComplete}
            />

            {/* Bulk Tag Editor Modal */}
            {isAdminMode && (
                <BulkTagEditor
                    drills={selectedDrillsArray}
                    isOpen={isBulkTagEditorOpen}
                    onClose={() => setIsBulkTagEditorOpen(false)}
                    onSave={async (drillIds, editedCommonTags) => {
                        if (drillIds.length === 0) {
                            toast({
                                title: "No drills selected",
                                description: "Choose one or more drills before editing tags.",
                                variant: "destructive"
                            });
                            return;
                        }

                        try {
                            // Get the original common tags to know which categories were common
                            const originalCommonTags = getCommonTags(selectedDrillsArray);
                            const originalCommonCategories = new Set(Object.keys(originalCommonTags));

                            // Apply edited tags to all selected drills, preserving unique tags
                            for (const drillId of drillIds) {
                                const drill = selectedDrillsArray.find(d => d.id === drillId);
                                if (!drill) continue;

                                // Start with the drill's existing tags
                                const updatedTags = { ...drill.tags };

                                // For each category in editedCommonTags, replace that category's values
                                // This updates only the common tags (or newly added tags), preserving unique tags
                                Object.keys(editedCommonTags).forEach(category => {
                                    const editedValues = editedCommonTags[category];
                                    if (editedValues && editedValues.length > 0) {
                                        // Replace the category with the edited values
                                        updatedTags[category] = editedValues.length === 1 ? editedValues[0] : editedValues;
                                    } else {
                                        // If the category is empty in edited tags, only remove it if it was originally common
                                        // This prevents removing unique tags that weren't in the editor
                                        if (originalCommonCategories.has(category)) {
                                            delete updatedTags[category];
                                        }
                                    }
                                });

                                // Handle categories that were common but are now removed from editedCommonTags
                                // (categories that were in originalCommonTags but not in editedCommonTags)
                                originalCommonCategories.forEach(category => {
                                    if (!(category in editedCommonTags)) {
                                        // This category was common but is no longer in edited tags, remove it
                                        delete updatedTags[category];
                                    }
                                });

                                await drillDb.updateDrill(drillId, { tags: updatedTags });
                            }

                            toast({
                                title: "Tags updated",
                                description: `Applied common tag changes to ${drillIds.length} drill${drillIds.length === 1 ? '' : 's'}. Unique tags were preserved.`
                            });
                            setIsBulkTagEditorOpen(false);
                        } catch (error) {
                            logger.error('Error updating tags in bulk:', error);
                            toast({
                                title: "Failed to update tags",
                                description: error instanceof Error ? error.message : 'Unknown error occurred.',
                                variant: "destructive"
                            });
                        }
                    }}
                />
            )}

        </div>
    );
};

export default DrillLibrary;
